import type { ReactNode } from 'react'
import { ReportSidebarProvider } from './ReportSidebarContext'
import { useReportSidebar } from './useReportSidebar'
import { useOptionalBranchSelectionContext } from '../../../../features/branchSelection'
import { ReportSidebar } from './index'

interface ReportSidebarFeatureProps {
  /** Children rendered within the provider - can use useReportSidebar() */
  children: ReactNode
  /** Callback when a node is clicked in the sidebar */
  onNodeClick?: (nodeId: string) => void
}

/**
 * Self-contained report sidebar feature.
 *
 * This component owns its own visibility state - parent doesn't need to manage it.
 * Children can use useReportSidebar() hook to control the sidebar.
 */
export function ReportSidebarFeature({ children, onNodeClick }: ReportSidebarFeatureProps) {
  const branchSelection = useOptionalBranchSelectionContext()

  return (
    <ReportSidebarProvider branchSelection={branchSelection}>
      {children}
      <ReportSidebarRenderer onNodeClick={onNodeClick} />
    </ReportSidebarProvider>
  )
}

/**
 * Internal component that renders the sidebar using context.
 * Separated to consume context within the provider.
 */
function ReportSidebarRenderer({ onNodeClick }: { onNodeClick?: (nodeId: string) => void }) {
  const { isOpen, close } = useReportSidebar()

  return <ReportSidebar isVisible={isOpen} onClose={close} onNodeClick={onNodeClick} />
}
